import { useState, useEffect, startTransition } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { Locale } from '../types/locale';
import { urlMappings, locales } from '../utils/hreflang';
import { getEnglishSlugSync, getLocalizedSlugSync } from '../blog/slugMapping';

const LOCALE_STORAGE_KEY = 'userPreferredLocale';

/**
 * Given a localized slug (without locale prefix), find the canonical English slug.
 * E.g., "kostenloser-ki-wasserzeichen-entferner" → "free-ai-watermark-remover"
 */
const findCanonicalSlug = (localizedSlug: string, fromLocale: Locale): string => {
  // If it's already in English format, it might be a direct match
  const withLeadingSlash = localizedSlug.startsWith('/') ? localizedSlug : `/${localizedSlug}`;

  // Search through urlMappings to find which English slug maps to this localized slug
  for (const [englishSlug, translations] of Object.entries(urlMappings)) {
    const localizedVersion = translations[fromLocale];
    if (localizedVersion === withLeadingSlash) {
      return englishSlug;
    }
  }

  // If no translation found, return the original (might already be English or unmapped)
  return withLeadingSlash;
};

/**
 * Get the localized slug for a given English slug and target locale.
 * E.g., ("free-ai-watermark-remover", "de") → "kostenloser-ki-wasserzeichen-entferner"
 */
const getLocalizedSlug = (englishSlug: string, toLocale: Locale): string => {
  const withLeadingSlash = englishSlug.startsWith('/') ? englishSlug : `/${englishSlug}`;

  const mapping = urlMappings[withLeadingSlash];
  if (mapping && mapping[toLocale]) {
    return mapping[toLocale];
  }

  // If no translation exists, return the original
  return withLeadingSlash;
};

/**
 * Detect locale from pathname synchronously.
 * This must run during initial render (not in useEffect) to prevent hydration mismatch.
 */
function detectLocaleFromPath(pathname: string): Locale {
  // Must match exact locale prefix followed by / or end of string
  // Critical: /detect should NOT match /de because it's not a German locale path
  if (pathname === '/my' || pathname.startsWith('/my/')) {
    return 'ms-MY';
  }
  if (pathname === '/pt-br' || pathname.startsWith('/pt-br/')) {
    return 'pt-BR';
  }
  if (pathname === '/es' || pathname.startsWith('/es/')) {
    return 'es';
  }
  if (pathname === '/de' || pathname.startsWith('/de/')) {
    return 'de';
  }
  if (pathname === '/fr' || pathname.startsWith('/fr/')) {
    return 'fr';
  }
  if (pathname === '/ja' || pathname.startsWith('/ja/')) {
    return 'ja';
  }
  if (pathname === '/id' || pathname.startsWith('/id/')) {
    return 'id';
  }
  // For all other paths (/, /blog, /detect, etc.), use English
  return 'en';
}

export const useLocale = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // CRITICAL FIX: Detect locale synchronously from URL during initial render.
  // This ensures SSR and client-side have matching locale values, preventing
  // hydration mismatch that was causing CLS on pSEO pages.
  // Previously: useState('en') + useEffect detection caused:
  // 1. SSR renders with locale='en' (useEffect doesn't run on server)
  // 2. Client initial render with locale='en'
  // 3. useEffect runs, detects real locale, triggers re-render → CLS
  const [locale, setLocale] = useState<Locale>(() =>
    detectLocaleFromPath(location.pathname)
  );

  useEffect(() => {
    // Only run on client side for side effects (DOM updates, localStorage)
    if (typeof window === 'undefined') return;

    // Detect locale from current path
    const detectedLocale = detectLocaleFromPath(location.pathname);

    // Only update state if locale changed (e.g., user navigated to different locale)
    // HYDRATION FIX: Wrap in startTransition to prevent React Error #421
    if (detectedLocale !== locale) {
      startTransition(() => {
        setLocale(detectedLocale);
      });
    }

    // Side effects: update HTML lang attribute and localStorage
    document.documentElement.lang = detectedLocale;
    localStorage.setItem(LOCALE_STORAGE_KEY, detectedLocale);
  }, [location.pathname, locale]);

  const changeLocale = (newLocale: Locale) => {
    if (typeof window !== 'undefined') {
      document.documentElement.lang = newLocale;
      // Save user's language preference BEFORE navigation to prevent race condition
      localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);

      // Update URL to reflect locale change
      const currentPath = location.pathname;

      // Detect current locale from path
      // Must match exact locale prefix followed by / or end of string
      let currentLocale: Locale = 'en';
      if (currentPath === '/my' || currentPath.startsWith('/my/')) {
        currentLocale = 'ms-MY';
      } else if (currentPath === '/pt-br' || currentPath.startsWith('/pt-br/')) {
        currentLocale = 'pt-BR';
      } else if (currentPath === '/es' || currentPath.startsWith('/es/')) {
        currentLocale = 'es';
      } else if (currentPath === '/de' || currentPath.startsWith('/de/')) {
        currentLocale = 'de';
      } else if (currentPath === '/fr' || currentPath.startsWith('/fr/')) {
        currentLocale = 'fr';
      } else if (currentPath === '/ja' || currentPath.startsWith('/ja/')) {
        currentLocale = 'ja';
      } else if (currentPath === '/id' || currentPath.startsWith('/id/')) {
        currentLocale = 'id';
      }

      // Remove existing locale prefix to get the slug
      // IMPORTANT: Must require / or end-of-string after locale to avoid matching /detect as /de + tect
      const pathWithoutLocale = currentPath.replace(/^\/(en|de|pt-br|my|es|fr|ja|id)(\/|$)/, '/').replace(/^\/$/, '') || '/';

      // Check if this is a blog post path and handle slug translation
      const blogMatch = pathWithoutLocale.match(/^\/blog\/(.+)$/);
      if (blogMatch) {
        const blogSlug = blogMatch[1];
        // Get English slug from current locale's slug (SYNC for reliability)
        const englishBlogSlug = getEnglishSlugSync(blogSlug, currentLocale);
        // Get localized slug for target locale (SYNC for reliability)
        const localizedBlogSlug = getLocalizedSlugSync(englishBlogSlug, newLocale);

        // Update state immediately
        setLocale(newLocale);

        // Build new path
        const localePrefix = locales[newLocale] || '';
        const newPath = `${localePrefix}/blog/${localizedBlogSlug}`;
        navigate(newPath);
        return;
      }

      // Find the canonical (English) slug for the current path
      const canonicalSlug = findCanonicalSlug(pathWithoutLocale, currentLocale);

      // Get the localized slug for the target locale
      const localizedSlug = getLocalizedSlug(canonicalSlug, newLocale);

      // Update state immediately to prevent flash of wrong locale
      setLocale(newLocale);

      // Build the new path with locale prefix
      const localePrefix = locales[newLocale] || '';
      const newPath = localizedSlug === '/'
        ? (localePrefix || '/')
        : `${localePrefix}${localizedSlug}`;

      navigate(newPath);
    }
  };

  return { locale, changeLocale };
};
